/* eslint-disable arrow-body-style */
import { DehydratedState } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { Session } from 'next-auth';
import { SSRConfig } from 'next-i18next';
import { handleAuthRedirect } from 'utils/ssr/middleware/authMiddleware';
import { handleCmsData } from 'utils/ssr/middleware/cmsMiddleware';
import { handleContextHash } from 'utils/ssr/middleware/contextHashMiddleware';
import { handleFeatureFlagMiddleware } from 'utils/ssr/middleware/featureFlagMiddleware';
import { handleFileExtensions } from 'utils/ssr/middleware/fileExtensionsMiddleware';
import { handleLocale } from 'utils/ssr/middleware/localeMiddleware';
import { handleRender } from 'utils/ssr/middleware/renderMiddleware';
import { handleUserContext } from 'utils/ssr/middleware/userContextMiddleware';
import LAYOUT_MAPPER from '../components/layouts/mapper';
import Head from '../components/organisms/Head/Head';
import TEMPLATE_MAPPER from '../components/template/mapper';
import { DtoPage } from '../types/cms';
import { useCmsData } from '../utils/cms';
import { withMiddleware } from '../utils/ssr/withMiddleware';
import NotFoundPage from './404';
import { usePageSetup } from '../hooks/usePageSetup';
import ErrorBoundaryWrapper from '@/organisms/ErrorBoundary';

export type SSRProps = {
  locale?: string | null;
  dehydratedState: DehydratedState;
  session?: Session | null;
  generalSettings?: DtoPage['generalSettings'] | null;
  isMobileApp: boolean;
  templateProps: Record<string, unknown>;
} & SSRConfig;

const WpEntry = ({ templateProps }: SSRProps) => {
  const data = useCmsData();
  const Template = data && TEMPLATE_MAPPER[data.template];
  const Layout = LAYOUT_MAPPER[data.acf.layout];
  usePageSetup(false);

  if (!Template || !Layout) {
    return <NotFoundPage />;
  }

  return (
    <Layout
      acf={data.acf}
      background={data.acf.background}
      template={data.template}
      useFooter={data.acf.footer !== 'hidden'}
      useHeader={data.acf.header !== 'hidden'}
    >
      <Head />
      <ErrorBoundaryWrapper>
        <Template {...templateProps} />
      </ErrorBoundaryWrapper>
    </Layout>
  );
};

export default WpEntry;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  return withMiddleware([
    handleFileExtensions,
    handleLocale,
    handleUserContext,
    handleContextHash,
    handleCmsData,
    handleFeatureFlagMiddleware,
    handleAuthRedirect,
    handleRender,
  ])(ctx);
};
